import React, { useEffect, useState } from "react";
import { Bell } from "lucide-react";

interface NotificationBellProps {
  unreadCount: number;
  renderPanel: (isOpen: boolean, onClose: () => void) => React.ReactNode;
  ariaLabel?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  unreadCount,
  renderPanel,
  ariaLabel = "Notifications",
}) => {
  const [bellOpen, setBellOpen] = useState(false);
  const [showBadgeAnimation, setShowBadgeAnimation] = useState(false);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);

  useEffect(() => {
    if (unreadCount > prevUnreadCount) {
      setShowBadgeAnimation(true);
      const timer = window.setTimeout(() => setShowBadgeAnimation(false), 500);
      return () => window.clearTimeout(timer);
    }
    setPrevUnreadCount(unreadCount);
  }, [unreadCount, prevUnreadCount]);

  const formatBadgeText = () => {
    if (unreadCount > 99) return "99+";
    return unreadCount.toString();
  };

  const badgeText = formatBadgeText();
  const shouldShowBadge = unreadCount > 0;

  const handleToggle = () => {
    setBellOpen((prev) => !prev);
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="relative p-3 text-slate-600 hover:text-primary bg-white/0 hover:bg-primary/10 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 border border-transparent hover:border-primary/20 shadow-sm hover:shadow-md"
        aria-label={`${ariaLabel}${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={bellOpen}
        aria-haspopup="true"
      >
        <Bell className="w-5 h-5" />

        {shouldShowBadge && (
          <span
            className={`absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-bold leading-none text-white bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-lg shadow-red-500/50 border border-white/20 ${showBadgeAnimation
              ? "animate-badge-pop"
              : "animate-badge-idle"
              }`}
            aria-label={`${unreadCount} unread notifications`}
          >
            {badgeText}
          </span>
        )}
      </button>

      {renderPanel(bellOpen, () => setBellOpen(false))}

      <style>{`
        @keyframes badge-pop {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes badge-idle {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .animate-badge-pop {
          animation: badge-pop 0.5s ease-out forwards;
        }

        .animate-badge-idle {
          animation: badge-idle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
